import { Link } from 'react-router-dom'
import { SEO, FAQSchema } from '@/components/seo'
import { Check, ArrowRight, Calendar } from 'lucide-react'
import { buttonStyles } from '@/components/ui'

const pricingFaq = [
  {
    question: 'How does the demo work?',
    answer: 'Book a 30-minute call with our team. We\'ll walk you through the platform, show you the data, and tailor a plan that fits your needs.',
  },
  {
    question: 'Is there a free tier?',
    answer: 'Yes! Our core tools — AI Detector, Arena, Leaderboard, and Metadata Cleaner — are free for everyone, no signup required.',
  },
  {
    question: 'What data do I get access to?',
    answer: 'Paid plans include full benchmark datasets, per-detector accuracy breakdowns, historical ELO trends, and raw detection scores across thousands of images.',
  },
  {
    question: 'Can I cancel anytime?',
    answer: 'Yes. Cancel anytime and keep access until the end of your billing period. No long-term contracts.',
  },
  {
    question: 'What payment methods do you accept?',
    answer: 'We accept all major credit cards and PayPal through our secure payment processor.',
  },
]

const plans = [
  {
    name: 'Free',
    description: 'Explore the platform',
    features: [
      'AI image detector — unlimited checks',
      'Arena — head-to-head detector battles',
      'Leaderboard — live ELO rankings',
      'Metadata Cleaner (EXIF, C2PA)',
      'Public benchmark results',
      'Web interface',
    ],
    cta: 'Get Started Free',
    href: '/',
    highlighted: false,
  },
  {
    name: 'Independent',
    description: 'For researchers, creators, and professionals',
    features: [
      'Everything in Free',
      'Full benchmark dataset access',
      'Per-detector accuracy breakdowns',
      'Historical ELO trends & analytics',
      'Raw detection scores export (CSV, JSON)',
      'API access — 1,000 requests/month',
      'Batch image processing',
      'Email support',
    ],
    cta: 'Book a Demo',
    href: '/contact',
    highlighted: true,
  },
  {
    name: 'Enterprise',
    description: 'For teams, platforms, and organizations',
    features: [
      'Everything in Independent',
      'Unlimited API requests',
      'Custom detector integrations',
      'Dedicated benchmark environments',
      'Team accounts & collaboration',
      'White-label reporting',
      'Priority support & SLA',
      'Custom data exports & webhooks',
    ],
    cta: 'Book a Demo',
    href: '/contact',
    highlighted: false,
  },
]

export function Pricing() {
  return (
    <>
      <SEO
        title="Pricing - AI Detector Arena"
        description="Free AI detection tools for everyone. Independent and Enterprise plans for full benchmark data, API access, and custom integrations. Book a demo today."
        canonical="/pricing"
        keywords="ai detector arena pricing, ai image detection api, ai benchmark data, ai detector comparison"
      />
      <FAQSchema questions={pricingFaq} />

      {/* Hero */}
      <section className="section bg-gradient-to-b from-primary-50 to-white">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-800">
              Free Tools. Premium Data.
            </h1>
            <p className="mt-4 text-lg text-gray-600">
              Core detection tools are free for everyone. Upgrade for full benchmark data,
              API access, and enterprise integrations.
            </p>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="section bg-white -mt-8">
        <div className="container">
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {plans.map((plan) => (
              <div
                key={plan.name}
                className={`card relative ${
                  plan.highlighted
                    ? 'border-2 border-primary-500 shadow-lg'
                    : 'border border-gray-200'
                }`}
              >
                {plan.highlighted && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-primary-500 text-white text-sm font-medium px-3 py-1 rounded-full">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-gray-800">{plan.name}</h3>
                  <p className="mt-2 text-sm text-gray-500">{plan.description}</p>
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to={plan.href}
                  className={buttonStyles({
                    variant: plan.highlighted ? 'primary' : 'secondary',
                    className: 'w-full',
                  })}
                >
                  {plan.cta}
                  {plan.name === 'Free' ? (
                    <ArrowRight className="w-4 h-4" />
                  ) : (
                    <Calendar className="w-4 h-4" />
                  )}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section bg-gray-50">
        <div className="container">
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-10">
            Frequently Asked Questions
          </h2>
          <div className="max-w-2xl mx-auto space-y-4">
            {pricingFaq.map((item, index) => (
              <details key={index} className="group card cursor-pointer bg-white">
                <summary className="flex items-center justify-between font-medium text-gray-800 list-none">
                  {item.question}
                  <span className="ml-4 text-gray-400 group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <p className="mt-4 text-gray-600">{item.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section bg-primary-600">
        <div className="container text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Not sure which plan fits?
          </h2>
          <p className="text-primary-100 mb-8">
            Book a free 30-minute demo. We'll show you the platform, the data, and help you choose.
          </p>
          <Link
            to="/contact"
            className={buttonStyles({
              variant: 'secondary',
              size: 'lg',
              className: 'bg-white text-primary-600 hover:bg-gray-100',
            })}
          >
            Book a Demo
            <Calendar className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </>
  )
}
